import {ErrorState, SentinelLoading} from 'presentation/components';
import {LoadingSkeleton} from 'presentation/pages/private/my-hospitalization/components/LoadingSkeleton';
import React, {useMemo} from 'react';
import {translations} from 'presentation/translations-new';
import {EmptySearchState} from 'presentation/pages/private/my-hospitalization/components/EmptySearchState';
import {LayoutContainer} from 'presentation/pages/private/my-hospitalization/components/LayoutContainer';
import {MyHospitalizationFilterHeaderType} from 'presentation/pages/private/my-hospitalization/components/Filter/types';
import {EmptyStateHospitalization} from 'presentation/pages/private/my-hospitalization/components/EmptyState';
import {useMyHospitalizationLoggingAccess} from 'presentation/pages/private/my-hospitalization/hooks/useMyHospitalizationLoggingAccess';
import {ActionTypeLoggingAccessEnum} from 'domain/usecases/private/my-hospitalization/post-logging-access';
import {ExamCardsGrid} from '../ExamsCardsGrid';
import {useRequestExams} from '../../hooks/useRequestExams';
import {tabs} from '../../constants/examsTabs';
import {filters} from '../../constants/examsFilters';
import {HeaderFilterExams} from '../HeaderFilterExams';

const {empty, error, searchEmpty, searchPlaceholder, titleListFilter} =
	translations['pt-br'].privateFlow.myHospitalization.exams;

export const RequestExams = () => {
	useMyHospitalizationLoggingAccess(ActionTypeLoggingAccessEnum.EXAMS);
	const {
		items,
		loadingItemsList,
		isEmptyFiltered,
		isLoading,
		itemsListIsEmpty,
		itemsListHasError,
		loadMore,
		refresh,
		searchValueState,
		debouncedQueryFunc,
		handleClear,
		abortDebounce,
	} = useRequestExams();

	const SEARCH_CONFIGS: MyHospitalizationFilterHeaderType = useMemo(
		() => ({
			searchProps: {
				placeholder: searchPlaceholder,
				clearSearch: () => handleClear('search'),
				handleChange: () => {
					abortDebounce();
					debouncedQueryFunc();
				},
				value: searchValueState,
			},
		}),
		[searchValueState],
	);

	if (isLoading) {
		return (
			<LayoutContainer filterConfig={SEARCH_CONFIGS} tabs={tabs}>
				<LoadingSkeleton />
			</LayoutContainer>
		);
	}

	if (itemsListHasError) {
		return (
			<LayoutContainer tabs={tabs}>
				<ErrorState
					title={error.title}
					description={error.description}
					handleRefresh={refresh}
				/>
			</LayoutContainer>
		);
	}

	const showEmptySearchState =
		(itemsListIsEmpty && searchValueState.length) || (!itemsListIsEmpty && isEmptyFiltered);

	if (showEmptySearchState) {
		return (
			<LayoutContainer filterConfig={SEARCH_CONFIGS} tabs={tabs}>
				<HeaderFilterExams
					listDrawerItens={filters}
					titleListDrawer={titleListFilter}
					count={items.length}
				/>
				<EmptySearchState
					title={searchEmpty.title}
					description={searchEmpty.description}
					clearFilter={() => handleClear('all')}
				/>
			</LayoutContainer>
		);
	}

	if (itemsListIsEmpty) {
		return (
			<LayoutContainer tabs={tabs}>
				<EmptyStateHospitalization
					iconName="clipboard-plus-outline"
					title={empty.title}
					description={empty.description}
				/>
			</LayoutContainer>
		);
	}

	return (
		<LayoutContainer filterConfig={SEARCH_CONFIGS} tabs={tabs}>
			<HeaderFilterExams
				listDrawerItens={filters}
				titleListDrawer={titleListFilter}
				count={items.length}
			/>
			<ExamCardsGrid list={items} />
			<SentinelLoading
				minHeight="200px"
				onVisible={() => {
					if (items?.length > 0) {
						loadMore();
					}
				}}
				isLoading={loadingItemsList}
			/>
		</LayoutContainer>
	);
};
